/**
 * API Configuration
 * Provides dynamic API URL resolution for different deployment environments
 */

// Get the backend URL - check runtime override first, then env variable
const getBackendUrlBase = () => {
  // Check for runtime override (set in index.js for production)
  if (typeof window !== 'undefined' && window.__RUNTIME_BACKEND_URL__) {
    return window.__RUNTIME_BACKEND_URL__;
  }
  
  // Check environment variables
  const envUrl = process.env.REACT_APP_BACKEND_URL || 
                 (typeof import.meta !== 'undefined' && import.meta.env?.REACT_APP_BACKEND_URL);
  
  if (envUrl && !envUrl.includes('pole-pro-1')) {
    return envUrl;
  }
  
  return null;
};

const BACKEND_URL = getBackendUrlBase();

/**
 * Dynamically resolve the API URL based on the current environment
 * @returns {string} The base API URL
 */
export const getApiUrl = () => {
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Check runtime override first
  if (typeof window !== 'undefined' && window.__RUNTIME_BACKEND_URL__) {
    return `${window.__RUNTIME_BACKEND_URL__}/api`;
  }
  
  // If running on localhost, always use localhost backend
  if (currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')) {
    return `${currentOrigin}/api`;
  }
  
  // For lightstreamengineering.com (production), use same-origin API
  // This ensures the frontend always calls its own backend, avoiding CORS issues
  if (currentOrigin.includes('lightstreamengineering.com')) {
    return `${currentOrigin}/api`;
  }
  
  // If we have a valid backend URL configured, use it
  if (BACKEND_URL) {
    const cleanUrl = BACKEND_URL.replace(/\/$/, '');
    return `${cleanUrl}/api`;
  }
  
  // Default: use relative path based on current origin
  return `${currentOrigin}/api`;
};

/**
 * Get the base API URL (includes /api prefix)
 */
export const API_URL = getApiUrl();

/**
 * Get the backend base URL (without /api prefix)
 */
export const getBackendUrl = () => {
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Check runtime override first
  if (typeof window !== 'undefined' && window.__RUNTIME_BACKEND_URL__) {
    return window.__RUNTIME_BACKEND_URL__;
  }
  
  // If running on localhost, use localhost
  if (currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')) {
    return currentOrigin;
  }
  
  // For lightstreamengineering.com (production), use same-origin
  if (currentOrigin.includes('lightstreamengineering.com')) {
    return currentOrigin;
  }
  
  if (BACKEND_URL) {
    return BACKEND_URL.replace(/\/$/, '');
  }
  return currentOrigin;
};

/**
 * Get the raw backend URL for components that need it
 * This replaces direct use of process.env.REACT_APP_BACKEND_URL
 */
export const getRawBackendUrl = () => {
  return getBackendUrl();
};

// Log the resolved API URL for debugging
console.log('🌐 API URL resolved to:', API_URL);
console.log('🔗 Backend URL:', getBackendUrl());

export default API_URL;
